import { Component, OnInit, AfterViewInit, ViewChild, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgDynamicBreadcrumbService } from 'ng-dynamic-breadcrumb';
import { FormBuilder, FormGroup, Validators, FormArray, AbstractControl, FormControl, ValidationErrors, ControlContainer, NgForm } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';

import { BHPService } from '../_services/bhp.service';
import { UserWellsService } from '../user-wells.service';
import { ExcelImportService } from '../_services/excel-import.service';

import { DailyDateRangeSelectorComponent } from '../daily-date-range-selector/daily-date-range-selector.component';
import { InjectionDataGrid2Component } from '../injection-data-grid2/injection-data-grid2.component';
import { ApiResultErrorDialogComponent } from '../api-result-error-dialog/api-result-error-dialog.component';
import { MessageDialogComponent } from '../message-dialog/message-dialog.component';
import { ImportExcelDataComponent } from '../import-excel-data/import-excel-data.component';

import { InjectionPressureDataBatch } from '../_models/injection-pressure-data-batch';
import { InjectionPressureDataRecord } from '../_models/injection-pressure-data-record';
import { WellDetailsModel } from '../_models/well-details-model';
import { UserSubmittableDataBatch } from '../_models/user-submittable-data-batch';

import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';

@Component({
  selector: 'app-add-bhp',
  templateUrl: './add-bhp.component.html',
  styleUrls: ['./add-bhp.component.css']
})
export class AddBHPComponent implements OnInit, OnDestroy {

  currentForm: FormGroup;
  page1Form: FormGroup;
  page2Form: FormGroup;

  dateBoundsSelectionForm: FormGroup;
  useOldDataForm: FormGroup;
  methodForm: FormGroup;
  bhpDataForm: FormGroup;

  excelSubmitted: boolean = false;

  wellId: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  beginDate: BehaviorSubject<Date> = new BehaviorSubject<Date>(null);
  endDate: BehaviorSubject<Date> = new BehaviorSubject<Date>(null);

  @ViewChild("dateRangeSelector") dateRangeSelector: DailyDateRangeSelectorComponent;
  @ViewChild("bhpDataGrid") bhpDataGrid: InjectionDataGrid2Component;

  beginDateSubscription: Subscription;
  endDateSubscription: Subscription;
  detailsWellSubscription: Subscription;
  submitBatchSubscription: Subscription;
  excelTemplateDataSubscription: Subscription;
  measurementMethodSubscription: Subscription;

  wellRecord: WellDetailsModel;
  bhpBatch: InjectionPressureDataBatch;

  dateRangeMode: string = "multi";

  //This one is the first page - injection volumes
  public bhpDataColumns: any = {};

  constructor(
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef,
    protected router: Router,
    protected actRoute: ActivatedRoute,
    protected breadcrumbService: NgDynamicBreadcrumbService,
    public bhpService: BHPService,
    public userWellService: UserWellsService,
    protected excelImportService: ExcelImportService,
    protected dialog: MatDialog
  ) {

    //Subscribe to the details well on the user service.
    this.detailsWellSubscription = this.userWellService.detailsWell.subscribe((x) => {

      this.wellRecord = x;

      if (this.dateBoundsSelectionForm != null) {
        var dbsForm = this.dateBoundsSelectionForm.get("dateBoundsSelectionForm");

        if (dbsForm != null) {
          dbsForm.get("WellRecord").setValue(x);
        }
      }

      if (x != null) {
        //Update the breadcrumbs to show the well UIC number.

        var breadcrumbdata = {
          Uicnumber: x.Uicnumber.toString()
        };

        this.breadcrumbService.updateBreadcrumbLabels(breadcrumbdata);
      }
    });
    
    //Subscribe to the wellId change event...
    this.wellId.subscribe((newWellId) => {
      this.userWellService.LoadDetailsWell(newWellId);
    });

    //Subscribe to the result parameter on the bhp service.
    this.submitBatchSubscription = this.bhpService.submitBatchResult.subscribe((x) => {

      if (x != null) {
        //If we successfully uploaded our injection data, navigate back to the well details page
        if (x.Success == true) {

          var successRoute = 'well/' + this.wellId.value;

          router.navigate([successRoute]);
        }
        else {
          var dialogRef = this.dialog.open(ApiResultErrorDialogComponent, {
            data: x
          });
        }
      }
    });

    this.excelTemplateDataSubscription = this.excelImportService.currentExcelTemplateData.subscribe((x: any) => {

      if (x != null && this.excelSubmitted) {

        let excelInputErrorList = [];

        x.Injections.forEach((injection: any, index) => {
          Object.keys(this.bhpDataColumns).forEach(key => {
            if (this.bhpDataColumns[key].disabled) {
              injection[key] = null;
            }
          })
          if (!this.checkDatesMatch(injection.MeasurementDate, this.bhpDataForm.value.InjectionRows[index].MeasurementDate)) {
            excelInputErrorList.push(`Invalid date provided: ${injection.MeasurementDate}. Please do not modify the date columns.`);
          }
        });

        if (excelInputErrorList.length == 0) {
          x.Injections.forEach((injection: any, index) => {
            this.bhpDataGrid.patchValues(injection, index);
          });
        } else {
          let excelErrorObj = {
            Reasons: excelInputErrorList
          }
          this.dialog.open(ApiResultErrorDialogComponent, { data: excelErrorObj });
        }

      }

    });

  }

  ngOnInit(): void {

    this.bhpService.getDataColumnsListPromise().then((result: any) => {
      [this.bhpDataColumns] = result;
    }).catch((error) => {
      //display error dialog and return user to mywells when call fails
      this.dialog.open(MessageDialogComponent, {
        data: {
          title: "Service Unavailable",
          message: "Something went wrong, please try again."
        }
      }).afterClosed().subscribe(() => {
        this.router.navigate(['/mywells']);
      });
    });

    //Create the date bounds form - this is the formgroup for the first page
    this.dateBoundsSelectionForm = this.formBuilder.group({
    });

    //
    this.useOldDataForm = this.formBuilder.group({
      'useOldInjectionValues': [true, []]
    });

    this.methodForm = this.formBuilder.group({
      'bhpMeasurementMethod': [null, Validators.required]
    });

    //
    this.measurementMethodSubscription = this.methodForm.controls['bhpMeasurementMethod'].valueChanges.subscribe((newValue) => {

      if(newValue == 1 || newValue == 2) {
        this.dateRangeMode = "single";
      }
      else if(newValue == 3) {
        this.dateRangeMode = "multi";
      }
    });


    //Compose the page 1 form.  We use this to tie all these forms together, so that their validation state can be used to lock the stepper from stepping until a valid state is reached.
    this.page1Form = this.formBuilder.group({});

    this.page1Form.addControl("dateBounds", this.dateBoundsSelectionForm);
    this.page1Form.addControl("useOldData", this.useOldDataForm);
    this.page1Form.addControl("methodForm", this.methodForm);

    this.page2Form = this.formBuilder.group({});
  }

  ngAfterViewInit(): void {
    //We need our whole form to be initialized before we do this, because all of it relies on this.
    this.wellId.next(this.actRoute.snapshot.params.wellId);

    this.initDateBoundsSelectionForm();

    this.initBhpDataForm();

    this.currentForm = this.page1Form;

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    if(this.beginDateSubscription != null) {
      this.beginDateSubscription.unsubscribe();

      this.beginDateSubscription = null;
    }

    if(this.endDateSubscription != null) {
      this.endDateSubscription.unsubscribe();

      this.endDateSubscription = null;
    }
    if (this.excelTemplateDataSubscription != null) {
      this.excelTemplateDataSubscription.unsubscribe();
    }
    this.excelTemplateDataSubscription = null;

    if(this.measurementMethodSubscription != null) {
      this.measurementMethodSubscription.unsubscribe();
    }
    this.measurementMethodSubscription = null;
    this.bhpService.currentBatch = new BehaviorSubject<UserSubmittableDataBatch>(null);
  }

  //Handle the stepper changing from one step to another.
  onStepChange(event: any) {

    if (event.selectedIndex == 0) {
      this.currentForm = this.page1Form;
    }
    if (event.selectedIndex == 1) {
      this.currentForm = this.page2Form;
    }

    //Are we switching away from the date selection page (index 0)?
    if(event.previouslySelectedIndex == 0)
    {
      this.initColumnDataWithMeasurementMethod();
      this.initDailyInjectionPressureBatch();
    }

    //Are we switching to the submit page?
    if(event.selectedIndex == 2)
    {
      this.doPresubmit();
    }
  }

  initBhpDataForm() {
    this.bhpDataForm = this.bhpDataGrid.injectionForm;

    this.page2Form.addControl("bhpDataForm", this.bhpDataForm);
  }

  //The set of columns
  initColumnDataWithMeasurementMethod() {
    let measurementMethodFormValue = this.methodForm.value;

    let method = measurementMethodFormValue.bhpMeasurementMethod;
    this.bhpDataColumns = this.bhpService.updateDataColumnsByMeasurementMethod(this.bhpDataColumns, method);
  }

  initDailyInjectionPressureBatch() {
    if(this.bhpService.currentInjectionPressureBatch.value == null || this.dateBoundsSelectionForm.dirty || this.useOldDataForm.dirty)
    {
      let dateBoundsFormResult = this.dateBoundsSelectionForm.value.dateBoundsSelectionForm;

      let bd = dateBoundsFormResult.BeginDate;
      let ed = dateBoundsFormResult.EndDate;

      let bUseOldInjectionValues = this.useOldDataForm.value.useOldInjectionValues;

      this.bhpService.GetEmptyDailySubmittableBatch(this.wellId.value, bd, ed, bUseOldInjectionValues);

      this.dateBoundsSelectionForm.markAsPristine();
      this.useOldDataForm.markAsPristine();
    }
  }

  initDateBoundsSelectionForm() {
    this.dateBoundsSelectionForm.addControl('dateBoundsSelectionForm', this.dateRangeSelector.dateBoundsSelectionForm);
   
    this.dateBoundsSelectionForm.controls.dateBoundsSelectionForm.get("BeginDate").valueChanges.subscribe((newvalue) => {
      this.beginDate.next(newvalue);
    });

    this.dateBoundsSelectionForm.controls.dateBoundsSelectionForm.get("EndDate").valueChanges.subscribe((newvalue) => {
      this.endDate.next(newvalue);
    });

    //We have to manually grab these values initially.
    this.beginDate.next(this.dateBoundsSelectionForm.controls.dateBoundsSelectionForm.get("BeginDate").value);
    this.endDate.next(this.dateBoundsSelectionForm.controls.dateBoundsSelectionForm.get("EndDate").value);
    
  }

  fillInjectionPressureWithFormContents(inj: InjectionPressureDataRecord, i: number) {

    //Capture the injection data values from the form
    let injectionPressureDataFormValues = this.bhpDataGrid.getValues();

    if(injectionPressureDataFormValues.InjectionRows[i] != null) {
      let rowData = injectionPressureDataFormValues.InjectionRows[i];

      let keys = Object.keys(rowData);

      keys.forEach((key) => {

        if(key != "MeasurementDate") {
          inj[key] = rowData[key];
        }
      });
    }
  }

  checkDatesMatch(date1, date2) {
    return new Date(date1).getTime() == new Date(date2).getTime();
  }

  getDataColumnslist() {
    let labelList = [];

    let dataColumnObjs = [this.bhpDataColumns];

    //interate over all dataColumns add all labels to an array
    dataColumnObjs.forEach(dataColumnObj => {
      Object.keys(dataColumnObj).forEach(key => {
        labelList.push({
          key,
          label: dataColumnObj[key].label,
          required: dataColumnObj[key].required
        });
      });
    });

    return labelList;
  }

  getExcelTemplate() {
    let dateBoundsFormResult = this.dateBoundsSelectionForm.value.dateBoundsSelectionForm;

    let dataColumnslist = this.getDataColumnslist();

    this.excelImportService.generateTemplate(this.wellRecord, dateBoundsFormResult.BeginDate, dateBoundsFormResult.EndDate, 'daily', 'BottomHolePressure', dataColumnslist, this.bhpDataForm.value.InjectionRows);
  }

  uploadExcelTemplate() {

    let dataColumnslist = this.getDataColumnslist();
    let config: any = {};
    config.data = {
      dataColumnsList: dataColumnslist,
      controllerName: 'injectionPressure'
    };
    this.dialog.open(ImportExcelDataComponent, config);
    this.excelSubmitted = true;
  }

  doPresubmit() {
    //Avoid a dumb crash, in case the injection batch is null for some reason.
    if(this.bhpService.currentBatch.value != null) {
      let curBatch: InjectionPressureDataBatch = <InjectionPressureDataBatch> this.bhpService.currentBatch.value;

      //We're not going to use forEach here, because we need the index as a variable anyway.
      for (let i=0; i < curBatch.Injections.length; i++) {
        let inj = <InjectionPressureDataRecord> curBatch.Injections[i];

        //Fill the injection record with data from the forms.
        this.fillInjectionPressureWithFormContents(inj, i);
      }

      //Do the presubmit validation
      this.bhpService.doPresubmitValidation(this.bhpService.currentBatch.value);
    }
  }

  doSubmit() {
    let batch = this.bhpService.currentBatch.value;

    if(batch != null) {

      this.bhpService.SubmitBatch(batch);
    }
  }

  goNext(stepper: MatStepper) {
    stepper.next();
  }

  goBack(stepper: MatStepper) {
    stepper.previous();
  }

}
